import { translate, translatePlural } from "@/i18n/translate";
import { t } from "@/lib/i18n";

/**
 * The overall verdict of a pizza split into the checks it is made of, as pizza-backend computes it
 * (pipeline.overall_evaluation): OK only when the photo quality, ingredients, extras, removals,
 * burnt, uncooked and pizza saver checks all pass. "No Pizza" and "Invalid Photo" evaluations are
 * cut short after detection / photo quality and carry only those parts.
 *
 * Titles, labels and reasons are texts in the current UI language (built at call time).
 */

export type CheckStatus = "OK" | "NOK" | "N/A";

export type VerdictCheckKey =
  | "photo"
  | "base"
  | "extras"
  | "removals"
  | "burnt"
  | "raw"
  | "saver";

export interface VerdictCheck {
  key: VerdictCheckKey;
  label: string;
  status: CheckStatus;
  /** why the check failed, short enough for a list row */
  reason?: string;
}

export type VerdictKind = "ok" | "nok" | "no_pizza" | "invalid_photo" | "not_evaluated";

export interface PizzaVerdict {
  /** the stored overall status (OK / NOK), N/A when not evaluated */
  status: CheckStatus;
  kind: VerdictKind;
  /** headline for the verdict block */
  title: string;
  checks: VerdictCheck[];
  /** the failed checks' reasons, in check order */
  reasons: string[];
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type Metadata = Record<string, any> | null | undefined;

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const list = (names: string[] | undefined | null) => (names ?? []).map(capitalize).join(", ");
const asStatus = (value: unknown): CheckStatus =>
  value === "OK" ? "OK" : value === "NOK" ? "NOK" : "N/A";

export function photoQualityReason(photoQuality: Record<string, any> | null | undefined): string | undefined {
  if (!photoQuality || photoQuality.status === "OK") return undefined;
  if (photoQuality.primary_reason) return t(photoQuality.primary_reason);
  const failed: string[] = photoQuality.failed_checks ?? [];
  return failed.length ? failed.map((key) => t(key)).join(", ") : translate("status.invalidPhoto");
}

export function pizzaVerdict(metadata: Metadata): PizzaVerdict {
  if (!metadata) {
    return {
      status: "N/A",
      kind: "not_evaluated",
      title: translate("status.notEvaluated"),
      checks: [],
      reasons: [],
    };
  }

  const overall = asStatus(metadata.overall_evaluation?.status);
  const classification = String(metadata.classification ?? "").toLowerCase();
  const photo = metadata.photo_quality_evaluation;
  const checks: VerdictCheck[] = [];

  if (photo) {
    checks.push({
      key: "photo",
      label: translate("verdict.check.photo"),
      status: asStatus(photo.status),
      reason: photoQualityReason(photo),
    });
  }

  if (classification === "no pizza") {
    return {
      status: overall === "N/A" ? "NOK" : overall,
      kind: "no_pizza",
      title: translate("status.noPizza"),
      checks,
      reasons: [translate("verdict.reason.noPizza")],
    };
  }
  if (classification === "invalid photo") {
    const reason = photoQualityReason(photo) ?? translate("status.invalidPhoto");
    return {
      status: overall === "N/A" ? "NOK" : overall,
      kind: "invalid_photo",
      title: translate("status.invalidPhoto"),
      checks,
      reasons: [reason],
    };
  }

  const components = metadata.components_evaluation;
  if (components?.base_components) {
    const base = components.base_components;
    // missing ones, and since 2026-10-31 the ones the pizza has beyond the order
    const failures = [
      base.missing?.length ? translate("verdict.reason.missing", { list: list(base.missing) }) : null,
      base.unexpected?.length ? translate("verdict.reason.unexpected", { list: list(base.unexpected) }) : null,
    ].filter(Boolean);
    checks.push({
      key: "base",
      label: translate("verdict.check.base"),
      status: asStatus(base.status),
      reason: failures.length ? failures.join(", ") : undefined,
    });
  }
  const extras = components?.extras;
  if (extras && (extras.expected?.length || extras.non_detectable?.length || extras.status === "NOK")) {
    checks.push({
      key: "extras",
      label: translate("verdict.check.extras"),
      status: asStatus(extras.status),
      reason: extras.missing?.length
        ? translatePlural("verdict.reason.missingExtras", extras.missing.length, {
            list: list(extras.missing),
          })
        : undefined,
    });
  }
  const removals = components?.removals;
  if (removals && (removals.found?.length || removals.not_expected?.length || removals.status === "NOK")) {
    checks.push({
      key: "removals",
      label: translate("verdict.check.removals"),
      status: asStatus(removals.status),
      reason: removals.found?.length
        ? translate("verdict.reason.notRemoved", { list: list(removals.found) })
        : undefined,
    });
  }

  const doneness = metadata.doneness_evaluation;
  if (doneness?.burnt !== undefined) {
    checks.push({
      key: "burnt",
      label: translate("verdict.check.burnt"),
      status: doneness.burnt.status ? "NOK" : "OK",
      reason: doneness.burnt.status ? translate("verdict.reason.burnt") : undefined,
    });
  }
  if (doneness?.raw !== undefined && doneness.raw.evaluated !== false) {
    checks.push({
      key: "raw",
      label: translate("verdict.check.raw"),
      status: doneness.raw.status ? "NOK" : "OK",
      reason: doneness.raw.status ? translate("verdict.reason.raw") : undefined,
    });
  }

  if (metadata.pizza_saver_evaluation) {
    const saver = asStatus(metadata.pizza_saver_evaluation.status);
    checks.push({
      key: "saver",
      label: translate("verdict.check.saver"),
      status: saver,
      reason: saver === "NOK" ? translate("verdict.reason.saverMissing") : undefined,
    });
  }

  const reasons = checks
    .filter((check) => check.status === "NOK")
    .map((check) => check.reason ?? translate("verdict.reason.checkFailed", { check: check.label }));

  return {
    status: overall,
    kind: overall === "OK" ? "ok" : overall === "NOK" ? "nok" : "not_evaluated",
    title:
      overall === "OK"
        ? translate("status.ok")
        : overall === "NOK"
          ? translate("status.nok")
          : translate("status.notEvaluated"),
    checks,
    reasons,
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */
